import { ref } from 'vue'
import { onBeforeRouteLeave, useRouter, type RouteLocationNormalized } from 'vue-router'

import type { Ref } from 'vue'

/**
 * 当 upload 可用（upload-disabled 为 false）时，离开当前路由会弹出确认弹窗。
 * - 选择「离开」：放行原路由跳转。
 * - 选择「上传」：取消跳转并触发上传回调，由调用方按当前功能执行具体上传逻辑。
 */
export function useUploadConfirmOnLeave(
  canUpload: Ref<boolean>,
  onUpload: () => void | Promise<void>,
) {
  const router = useRouter()
  const isConfirmDialogOpen = ref(false)

  // 暂存被拦截的目标路由；用户确认离开时再放行。
  let pendingRoute: RouteLocationNormalized | null = null
  // 用户确认离开后，需要绕过一次守卫以放行原跳转。
  let bypassGuard = false

  onBeforeRouteLeave((to) => {
    if (bypassGuard) {
      bypassGuard = false
      return true
    }

    if (!canUpload.value) {
      return true
    }

    pendingRoute = to
    isConfirmDialogOpen.value = true
    return false
  })

  /** 用户选择离开：放行原目标路由 */
  const confirmLeave = () => {
    isConfirmDialogOpen.value = false
    const target = pendingRoute
    pendingRoute = null
    if (!target) return

    bypassGuard = true
    void router.push(target.fullPath)
  }

  /** 用户选择上传：取消跳转并触发上传 */
  const confirmUpload = async () => {
    isConfirmDialogOpen.value = false
    pendingRoute = null
    await onUpload()
  }

  return {
    isConfirmDialogOpen,
    confirmLeave,
    confirmUpload,
  }
}
